import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixAddonsV1AddOn from './addons-v1-add-on-add-ons.http.js';
// @ts-ignore
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

/** Add-ons are optional extras that extend business offerings (such as booking services) with additional features, time, or products for an extra cost. */
export interface AddOn extends AddOnAddOnInfoOneOf {
  /**
   * Duration in minutes for duration-based add-ons that extend offering time.
   * @min 1
   * @max 1440
   */
  durationInMinutes?: number;
  /**
   * Maximum quantity customers can purchase for quantity-based add-ons.
   * @min 1
   * @max 9999
   */
  maxQuantity?: number;
  /**
   * Add-on ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the add-on is updated.
   * To prevent conflicting changes, the current revision must be passed when updating the add-on.
   *
   * Ignored when creating an add-on.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the add-on was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the add-on was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Display name of the add-on shown to customers.
   * @minLength 1
   * @maxLength 100
   */
  name?: string | null;
  /** Pricing information for the add-on. */
  price?: Money;
  /**
   * ID of the [tax group](https://dev.wix.com/docs/rest/business-solutions/e-commerce/extensions/tax/tax-groups/introduction) to categorize the add-on for tax calculation in [Wix eCommerce](https://dev.wix.com/docs/rest/business-solutions/e-commerce/introduction).
   * Learn more about [how Wix eCommerce implements tax calculation](https://dev.wix.com/docs/rest/business-solutions/e-commerce/extensions/tax/introduction).
   *
   * This field doesn't apply to custom checkout/billing implementations.
   * @format GUID
   */
  taxGroup?: string | null;
  /** Data extensions enabling users to save custom data related to the add-on. */
  extendedFields?: ExtendedFields;
  /** [Tags](https://dev.wix.com/docs/rest/business-management/tags/introduction) assigned to add-ons for categorization and filtering purposes. */
  tags?: Tags;
}

/** @oneof */
export interface AddOnAddOnInfoOneOf {
  /**
   * Duration in minutes for duration-based add-ons that extend offering time.
   * @min 1
   * @max 1440
   */
  durationInMinutes?: number;
  /**
   * Maximum quantity customers can purchase for quantity-based add-ons.
   * @min 1
   * @max 9999
   */
  maxQuantity?: number;
}

/**
 * Money.
 * Default format to use. Sufficiently compliant with majority of standards: w3c, ISO 4217, ISO 20022, ISO 8583:2003.
 */
export interface Money {
  /**
   * Add-on price amount as a decimal string with period as decimal separator. For example, `3.99` for a $3.99 add-on or `15.00` for a $15.00 add-on.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0, lte:1000000000000000, maxScale:2 }
   */
  value?: string;
  /**
   * Currency code for the add-on price. Must be a valid [ISO 4217](https://en.wikipedia.org/wiki/ISO_4217) currency code. For example, `USD` for US dollars or `EUR` for euros.
   * @format CURRENCY
   */
  currency?: string;
  /**
   * Add-on price amount as a decimal string in local format for display purposes. For example, `$3.99` for US format or `3,99 €` for European format.
   * @maxLength 50
   */
  formattedValue?: string | null;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

/**
 * Common object for tags.
 * Should be use as in this example:
 * message Foo {
 * string id = 1;
 * ...
 * Tags tags = 5
 * }
 *
 * example of taggable entity
 * {
 * id: "123"
 * tags: {
 * tags: {
 * tag_ids:["11","22"]
 * },
 * private_tags: {
 * tag_ids: ["33", "44"]
 * }
 * }
 * }
 */
export interface Tags {
  /** Tags that require an additional permission in order to access them, normally not given to site members or visitors. */
  privateTags?: TagList;
  /** Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors. */
  tags?: TagList;
}

export interface TagList {
  /**
   * List of tag IDs.
   * @maxSize 100
   * @maxLength 5
   */
  tagIds?: string[];
}

export interface CreateAddOnRequest {
  /** Add-on to create. */
  addOn: AddOn;
}

export interface CreateAddOnResponse {
  /** Created add-on. */
  addOn?: AddOn;
}

export interface GetAddOnRequest {
  /**
   * ID of the add-on to retrieve.
   * @format GUID
   */
  addOnId: string;
}

export interface GetAddOnResponse {
  /** Retrieved add-on. */
  addOn?: AddOn;
}

export interface UpdateAddOnRequest {
  /** Add-on to update. */
  addOn: AddOn;
}

export interface UpdateAddOnResponse {
  /** Updated add-on. */
  addOn?: AddOn;
}

export interface DeleteAddOnRequest {
  /**
   * ID of the add-on to delete.
   * @format GUID
   */
  addOnId: string;
}

export interface DeleteAddOnResponse {}

export interface QueryAddOnsRequest {
  /** Filtering, sorting, and paging details. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   *
   * Refer to [supported filters](https://dev.wix.com/docs/rest/business-solutions/bookings/services/add-ons/supported-filters) for a complete list of supported fields.
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryAddOnsResponse {
  /** List of add-ons matching the query. */
  addOns?: AddOn[];
  /** Paging metadata for the query results. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface QueryLocalizedAddOnsRequest {
  /** WQL expression. */
  query?: CursorQuery;
}

export interface QueryLocalizedAddOnsResponse {
  /** List of AddOns. */
  addOns?: AddOn[];
  /** List of translated AddOns. */
  translatedAddOns?: AddOn[];
  /** Paging metadata */
  pagingMetadata?: CursorPagingMetadata;
}

export interface BulkCreateAddOnsRequest {
  /**
   * List of add-ons to create.
   * @minSize 1
   * @maxSize 100
   */
  addOns: AddOn[];
  /** Whether to return the created add-ons in the response. */
  returnEntity?: boolean;
}

export interface BulkCreateAddOnsResponse {
  /**
   * Results of the bulk create operation including add-ons and metadata.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkAddOnResult[];
  /** Metadata for the bulk create operation. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface ItemMetadata {
  /**
   * Item ID. Should always be available, unless it's impossible (for example, when failing to create an item).
   * @format GUID
   */
  _id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkAddOnResult {
  /** Metadata for the individual create operation. */
  itemMetadata?: ItemMetadata;
  /** Created add-on. Only present if `return_entity` was set to true in the request. */
  item?: AddOn;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface BulkUpdateAddOnsRequest {
  /**
   * List of add-ons to update.
   * @minSize 1
   * @maxSize 100
   */
  addOns: MaskedAddOn[];
  /** Whether to return the updated add-ons in the response. */
  returnEntity?: boolean;
}

export interface MaskedAddOn {
  /** Add-on to update. May be partial. */
  addOn?: AddOn;
}

export interface BulkUpdateAddOnsResponse {
  /**
   * Results of the bulk update operation.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkUpdateAddOnsResponseBulkAddOnResult[];
  /** Metadata for the bulk update operation. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkUpdateAddOnsResponseBulkAddOnResult {
  /** Metadata for the individual update operation. */
  itemMetadata?: ItemMetadata;
  /** Updated add-on. Only present if `return_entity` was set to true in the request. */
  item?: AddOn;
}

export interface BulkDeleteAddOnsRequest {
  /**
   * IDs of add-ons to delete.
   * @minSize 1
   * @maxSize 100
   * @format GUID
   */
  addOnIds: string[];
}

export interface BulkDeleteAddOnsResponse {
  /**
   * Results of the bulk delete operation.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkDeleteAddOnsResponseBulkAddOnResult[];
  /** Metadata for the bulk delete operation. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkDeleteAddOnsResponseBulkAddOnResult {
  /** Metadata for the individual delete operation. */
  itemMetadata?: ItemMetadata;
}

export interface BulkUpdateAddOnTagsRequest {
  /**
   * IDs of add-ons to update tags for.
   * @minSize 1
   * @maxSize 100
   * @format GUID
   */
  addOnIds?: string[];
  /** Tags to assign to the add-ons. */
  assignTags?: Tags;
  /** Tags to remove from the add-ons. */
  unassignTags?: Tags;
}

export interface BulkUpdateAddOnTagsResponse {
  /**
   * Results of the bulk tag update operation.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkUpdateAddOnTagsResult[];
  /** Metadata for the bulk tag update operation. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkUpdateAddOnTagsResult {
  /** Metadata for the individual tag update operation. */
  itemMetadata?: ItemMetadata;
}

export interface BulkUpdateAddOnTagsByFilterRequest {
  /** Filter to select add-ons for tag updates. */
  filter?: Record<string, any> | null;
  /** Tags to assign to the filtered add-ons. */
  assignTags?: Tags;
  /** Tags to remove from the filtered add-ons. */
  unassignTags?: Tags;
}

export interface BulkUpdateAddOnTagsByFilterResponse {
  /**
   * Job ID for tracking the asynchronous operation.
   * @format GUID
   */
  jobId?: string;
}

/** Encapsulates all details written to the Greyhound topic when a site's properties are updated. */
export interface SitePropertiesNotification {
  /** The site ID for which this update notification applies. */
  metasiteId?: string;
  /** The actual update event. */
  event?: SitePropertiesEvent;
  /**
   * A convenience set of mappings from the MetaSite ID to its constituent services.
   * @maxSize 500
   */
  translations?: Translation[];
  /** Context of the notification */
  changeContext?: ChangeContext;
}

/** The actual update event for a particular notification. */
export interface SitePropertiesEvent {
  /** Version of the site's properties represented by this update. */
  version?: number;
  /** Set of properties that were updated - corresponds to the fields in "properties". */
  fields?: string[];
  /** Updated properties. */
  properties?: Properties;
}

export interface Properties {
  /** Site categories. */
  categories?: Categories;
  /** Site locale. */
  locale?: Locale;
  /**
   * Site language.
   *
   * Two-letter language code in [ISO 639-1 alpha-2](https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes) format.
   */
  language?: string | null;
  /**
   * Site currency format used to bill customers.
   *
   * Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.
   */
  paymentCurrency?: string | null;
  /** Timezone in `America/New_York` format. */
  timeZone?: string | null;
  /** Email address. */
  email?: string | null;
  /** Phone number. */
  phone?: string | null;
  /** Fax number. */
  fax?: string | null;
  /** Address. */
  address?: Address;
  /** Site display name. */
  siteDisplayName?: string | null;
  /** Business name. */
  businessName?: string | null;
  /** Path to the site's logo in Wix Media (without Wix Media base URL). */
  logo?: string | null;
  /** Site description. */
  description?: string | null;
  /**
   * Business schedule. Regular and exceptional time periods when the business is open or the service is available.
   *
   * __Note:__ Not supported by Wix Bookings.
   */
  businessSchedule?: BusinessSchedule;
  /** Supported languages of a site and the primary language. */
  multilingual?: Multilingual;
  /** Cookie policy the Wix user defined for their site (before the site visitor interacts with/limits it). */
  consentPolicy?: ConsentPolicy;
  /**
   * Supported values: `FITNESS SERVICE`, `RESTAURANT`, `BLOG`, `STORE`, `EVENT`, `UNKNOWN`.
   *
   * Site business type.
   */
  businessConfig?: string | null;
  /** External site URL that uses Wix as its headless business solution. */
  externalSiteUrl?: string | null;
  /** Track clicks analytics. */
  trackClicksAnalytics?: boolean;
  /**
   * Company ID issued to the business by local authorities. Appears on invoices and price quotes.
   * @maxLength 50
   */
  companyId?: string | null;
}

export interface Categories {
  /** Primary site category. */
  primary?: string;
  /**
   * Secondary site category.
   * @maxSize 50
   */
  secondary?: string[];
  /** Business Term Id */
  businessTermId?: string | null;
}

export interface Locale {
  /** Two-letter language code in [ISO 639-1 alpha-2](https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes) format. */
  languageCode?: string;
  /** Two-letter country code in [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2#Officially_assigned_code_elements) format. */
  country?: string;
}

export interface Address {
  /** Street name. */
  street?: string;
  /** City name. */
  city?: string;
  /** Two-letter country code in an [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format. */
  country?: string;
  /** State. */
  state?: string;
  /**
   * Zip or postal code.
   * @maxLength 20
   */
  zip?: string;
  /** Extra information to be displayed in the address. */
  hint?: AddressHint;
  /** Whether this address represents a physical location. */
  isPhysical?: boolean;
  /** Google-formatted version of this address. */
  googleFormattedAddress?: string;
  /** Street number. */
  streetNumber?: string;
  /** Apartment number. */
  apartmentNumber?: string;
  /** Geographic coordinates of location. */
  coordinates?: GeoCoordinates;
}

/**
 * Extra information on displayed addresses.
 * This is used for display purposes. Used to add additional data about the address, such as "In the passage".
 * Free text. In addition, the user can state where to display the additional description - before, after, or instead of the address string.
 */
export interface AddressHint {
  /** Extra text displayed next to, or instead of, the actual address. */
  text?: string;
  /** Where the extra text should be displayed. */
  placement?: PlacementTypeWithLiterals;
}

/** Where the extra text should be displayed: before, after or instead of the actual address. */
export enum PlacementType {
  BEFORE = 'BEFORE',
  AFTER = 'AFTER',
  REPLACE = 'REPLACE',
}

/** @enumType */
export type PlacementTypeWithLiterals =
  | PlacementType
  | 'BEFORE'
  | 'AFTER'
  | 'REPLACE';

/** Geocoordinates for a particular address. */
export interface GeoCoordinates {
  /** Latitude of the location. Must be between -90 and 90. */
  latitude?: number;
  /** Longitude of the location. Must be between -180 and 180. */
  longitude?: number;
}

/** Business schedule. Regular and exceptional time periods when the business is open or the service is available. */
export interface BusinessSchedule {
  /**
   * Weekly recurring time periods when the business is regularly open or the service is available. Limited to 100 time periods.
   * @maxSize 100
   */
  periods?: TimePeriod[];
  /**
   * Exceptions to the business's regular hours. The business can be open or closed during the exception.
   * @maxSize 100
   */
  specialHourPeriod?: SpecialHourPeriod[];
}

/** Weekly recurring time periods when the business is regularly open or the service is available. */
export interface TimePeriod {
  /** Day of the week the period starts on. */
  openDay?: DayOfWeekWithLiterals;
  /**
   * Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are `00:00` to `24:00`, where `24:00` represents
   * midnight at the end of the specified day.
   */
  openTime?: string;
  /** Day of the week the period ends on. */
  closeDay?: DayOfWeekWithLiterals;
  /**
   * Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are `00:00` to `24:00`, where `24:00` represents
   * midnight at the end of the specified day.
   *
   * __Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.
   */
  closeTime?: string;
}

/** Enumerates the days of the week. */
export enum DayOfWeek {
  MONDAY = 'MONDAY',
  TUESDAY = 'TUESDAY',
  WEDNESDAY = 'WEDNESDAY',
  THURSDAY = 'THURSDAY',
  FRIDAY = 'FRIDAY',
  SATURDAY = 'SATURDAY',
  SUNDAY = 'SUNDAY',
}

/** @enumType */
export type DayOfWeekWithLiterals =
  | DayOfWeek
  | 'MONDAY'
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY'
  | 'SUNDAY';

/** Exception to the business's regular hours. The business can be open or closed during the exception. */
export interface SpecialHourPeriod {
  /** Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time). */
  startDate?: string;
  /** End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time). */
  endDate?: string;
  /**
   * Whether the business is closed (or the service is not available) during the exception.
   *
   * Default: `true`.
   */
  isClosed?: boolean;
  /** Additional info about the exception. For example, "We close earlier on New Year's Eve." */
  comment?: string;
}

export interface Multilingual {
  /**
   * Supported languages list.
   * @maxSize 200
   */
  supportedLanguages?: SupportedLanguage[];
  /** Whether to redirect to user language. */
  autoRedirect?: boolean;
}

export interface SupportedLanguage {
  /** Two-letter language code in [ISO 639-1 alpha-2](https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes) format. */
  languageCode?: string;
  /** Locale. */
  locale?: Locale;
  /** Whether the supported language is the primary language for the site. */
  isPrimary?: boolean;
  /** Language icon. */
  countryCode?: string;
  /** How the language will be resolved. For internal use. */
  resolutionMethod?: ResolutionMethodWithLiterals;
  /** Whether the supported language is the primary language for site visitors. */
  isVisitorPrimary?: boolean | null;
}

export enum ResolutionMethod {
  QUERY_PARAM = 'QUERY_PARAM',
  SUBDOMAIN = 'SUBDOMAIN',
  SUBDIRECTORY = 'SUBDIRECTORY',
}

/** @enumType */
export type ResolutionMethodWithLiterals =
  | ResolutionMethod
  | 'QUERY_PARAM'
  | 'SUBDOMAIN'
  | 'SUBDIRECTORY';

export interface ConsentPolicy {
  /** Whether the site uses cookies that are essential to site operation. Always `true`. */
  essential?: boolean | null;
  /** Whether the site uses cookies that affect site performance and other functional measurements. */
  functional?: boolean | null;
  /** Whether the site uses cookies that collect analytics about how the site is used (in order to improve it). */
  analytics?: boolean | null;
  /** Whether the site uses cookies that collect information allowing better customization of the experience for a current visitor. */
  advertising?: boolean | null;
  /** CCPA compliance flag. */
  dataToThirdParty?: boolean | null;
}

/** A single mapping from the MetaSite ID to a particular service. */
export interface Translation {
  /** The service type. */
  serviceType?: string;
  /** The application definition ID; this only applies to services of type ThirdPartyApps. */
  appDefId?: string;
  /** The instance ID of the service. */
  instanceId?: string;
}

export interface ChangeContext extends ChangeContextPayloadOneOf {
  /** Properties were updated. */
  propertiesChange?: PropertiesChange;
  /** Default properties were created on site creation. */
  siteCreated?: SiteCreated;
  /** Properties were cloned on site cloning. */
  siteCloned?: SiteCloned;
}

/** @oneof */
export interface ChangeContextPayloadOneOf {
  /** Properties were updated. */
  propertiesChange?: PropertiesChange;
  /** Default properties were created on site creation. */
  siteCreated?: SiteCreated;
  /** Properties were cloned on site cloning. */
  siteCloned?: SiteCloned;
}

export interface PropertiesChange {}

export interface SiteCreated {
  /** Origin template site id. */
  originTemplateId?: string | null;
}

export interface SiteCloned {
  /** Origin site id. */
  originMetaSiteId?: string;
}

export interface Empty {}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface AddOnCreatedEnvelope {
  entity: AddOn;
  metadata: EventMetadata;
}

/**
 * Triggered when an add-on is created.
 * @permissionScope Wix Multilingual - Nile Wrapper Domain Events Read
 * @permissionScopeId SCOPE.MULTILINGUAL.NILE_WRAPPER_DOMAIN_EVENTS_READ
 * @permissionScope Manage Bookings
 * @permissionScopeId SCOPE.DC-BOOKINGS.MANAGE-BOOKINGS
 * @permissionScope Manage Bookings - all permissions
 * @permissionScopeId SCOPE.DC-BOOKINGS-MEGA.MANAGE-BOOKINGS
 * @permissionId ADD_ONS.ADD_ON_DOMAIN_EVENTS_READ
 * @webhook
 * @eventType wix.addons.v1.add_on_created
 * @slug created
 * @documentationMaturity preview
 */
export declare function onAddOnCreated(
  handler: (event: AddOnCreatedEnvelope) => void | Promise<void>
): void;

export interface AddOnDeletedEnvelope {
  entity: AddOn;
  metadata: EventMetadata;
}

/**
 * Triggered when an add-on is deleted.
 * @permissionScope Wix Multilingual - Nile Wrapper Domain Events Read
 * @permissionScopeId SCOPE.MULTILINGUAL.NILE_WRAPPER_DOMAIN_EVENTS_READ
 * @permissionScope Manage Bookings
 * @permissionScopeId SCOPE.DC-BOOKINGS.MANAGE-BOOKINGS
 * @permissionScope Manage Bookings - all permissions
 * @permissionScopeId SCOPE.DC-BOOKINGS-MEGA.MANAGE-BOOKINGS
 * @permissionId ADD_ONS.ADD_ON_DOMAIN_EVENTS_READ
 * @webhook
 * @eventType wix.addons.v1.add_on_deleted
 * @slug deleted
 * @documentationMaturity preview
 */
export declare function onAddOnDeleted(
  handler: (event: AddOnDeletedEnvelope) => void | Promise<void>
): void;

export interface AddOnUpdatedEnvelope {
  entity: AddOn;
  metadata: EventMetadata;
  /** @hidden */
  modifiedFields: Record<string, any>;
}

/**
 * Triggered when an add-on is updated.
 * @permissionScope Wix Multilingual - Nile Wrapper Domain Events Read
 * @permissionScopeId SCOPE.MULTILINGUAL.NILE_WRAPPER_DOMAIN_EVENTS_READ
 * @permissionScope Manage Bookings
 * @permissionScopeId SCOPE.DC-BOOKINGS.MANAGE-BOOKINGS
 * @permissionScope Manage Bookings - all permissions
 * @permissionScopeId SCOPE.DC-BOOKINGS-MEGA.MANAGE-BOOKINGS
 * @permissionId ADD_ONS.ADD_ON_DOMAIN_EVENTS_READ
 * @webhook
 * @eventType wix.addons.v1.add_on_updated
 * @slug updated
 * @documentationMaturity preview
 */
export declare function onAddOnUpdated(
  handler: (event: AddOnUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * Creates an add-on.
 * @param addOn - Add-on to create.
 * @public
 * @documentationMaturity preview
 * @requiredField addOn
 * @permissionId ADD_ONS.ADD_ON_CREATE
 * @applicableIdentity APP
 * @returns Created add-on.
 * @fqn wix.addons.v1.AddOnsService.CreateAddOn
 */
export async function createAddOn(
  addOn: AddOn
): Promise<
  NonNullablePaths<
    AddOn,
    | `durationInMinutes`
    | `maxQuantity`
    | `price.value`
    | `price.currency`
    | `tags.privateTags.tagIds`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ addOn: addOn });

  const reqOpts = ambassadorWixAddonsV1AddOn.createAddOn(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.addOn!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { addOn: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['addOn']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves an add-on.
 * @param addOnId - ID of the add-on to retrieve.
 * @public
 * @documentationMaturity preview
 * @requiredField addOnId
 * @permissionId ADD_ONS.ADD_ON_READ
 * @applicableIdentity APP
 * @returns Retrieved add-on.
 * @fqn wix.addons.v1.AddOnsService.GetAddOn
 */
export async function getAddOn(
  addOnId: string
): Promise<
  NonNullablePaths<
    AddOn,
    | `durationInMinutes`
    | `maxQuantity`
    | `price.value`
    | `price.currency`
    | `tags.privateTags.tagIds`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ addOnId: addOnId });

  const reqOpts = ambassadorWixAddonsV1AddOn.getAddOn(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.addOn!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { addOnId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['addOnId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Updates an existing add-on.
 *
 * Each time an add-on is updated, its revision number increments by 1.
 * The current revision must be passed when updating to ensure you're working with the latest version and prevent unintended overwrites.
 * @param _id - Add-on ID.
 * @public
 * @documentationMaturity preview
 * @requiredField _id
 * @requiredField addOn
 * @requiredField addOn.revision
 * @permissionId ADD_ONS.ADD_ON_UPDATE
 * @applicableIdentity APP
 * @returns Updated add-on.
 * @fqn wix.addons.v1.AddOnsService.UpdateAddOn
 */
export async function updateAddOn(
  _id: string,
  addOn: NonNullablePaths<UpdateAddOn, `revision`, 2>
): Promise<
  NonNullablePaths<
    AddOn,
    | `durationInMinutes`
    | `maxQuantity`
    | `price.value`
    | `price.currency`
    | `tags.privateTags.tagIds`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    addOn: { ...addOn, id: _id },
  });

  const reqOpts = ambassadorWixAddonsV1AddOn.updateAddOn(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.addOn!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { addOn: '$[1]' },
        explicitPathsToArguments: { 'addOn.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'addOn']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateAddOn {
  /**
   * Duration in minutes for duration-based add-ons that extend offering time.
   * @min 1
   * @max 1440
   */
  durationInMinutes?: number;
  /**
   * Maximum quantity customers can purchase for quantity-based add-ons.
   * @min 1
   * @max 9999
   */
  maxQuantity?: number;
  /**
   * Add-on ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the add-on is updated.
   * To prevent conflicting changes, the current revision must be passed when updating the add-on.
   *
   * Ignored when creating an add-on.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the add-on was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the add-on was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Display name of the add-on shown to customers.
   * @minLength 1
   * @maxLength 100
   */
  name?: string | null;
  /** Pricing information for the add-on. */
  price?: Money;
  /**
   * ID of the [tax group](https://dev.wix.com/docs/rest/business-solutions/e-commerce/extensions/tax/tax-groups/introduction) to categorize the add-on for tax calculation in [Wix eCommerce](https://dev.wix.com/docs/rest/business-solutions/e-commerce/introduction).
   * Learn more about [how Wix eCommerce implements tax calculation](https://dev.wix.com/docs/rest/business-solutions/e-commerce/extensions/tax/introduction).
   *
   * This field doesn't apply to custom checkout/billing implementations.
   * @format GUID
   */
  taxGroup?: string | null;
  /** Data extensions enabling users to save custom data related to the add-on. */
  extendedFields?: ExtendedFields;
  /** [Tags](https://dev.wix.com/docs/rest/business-management/tags/introduction) assigned to add-ons for categorization and filtering purposes. */
  tags?: Tags;
}

/**
 * Deletes an add-on and removes it from all add-on groups.
 * @param addOnId - ID of the add-on to delete.
 * @public
 * @documentationMaturity preview
 * @requiredField addOnId
 * @permissionId ADD_ONS.ADD_ON_DELETE
 * @applicableIdentity APP
 * @fqn wix.addons.v1.AddOnsService.DeleteAddOn
 */
export async function deleteAddOn(addOnId: string): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ addOnId: addOnId });

  const reqOpts = ambassadorWixAddonsV1AddOn.deleteAddOn(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { addOnId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['addOnId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves up to 100 add-ons, given the specified filtering, sorting, and paging.
 *
 * By default, results are sorted by `id` in ascending order.
 * @public
 * @documentationMaturity preview
 * @permissionId ADD_ONS.ADD_ON_READ
 * @applicableIdentity APP
 * @fqn wix.addons.v1.AddOnsService.QueryAddOns
 */
export function queryAddOns(): AddOnsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<AddOn, 'CURSOR', QueryAddOnsRequest, QueryAddOnsResponse>(
    {
      func: async (payload: QueryAddOnsRequest) => {
        const reqOpts = ambassadorWixAddonsV1AddOn.queryAddOns(payload);

        sideEffects?.onSiteCall?.();
        try {
          const result = await httpClient.request(reqOpts);
          sideEffects?.onSuccess?.(result);
          return result;
        } catch (err) {
          sideEffects?.onError?.(err);
          throw err;
        }
      },
      requestTransformer: (query: QueryAddOnsRequest['query']) => {
        const args = [query, {}] as [QueryAddOnsRequest['query'], {}];
        return renameKeysFromSDKRequestToRESTRequest({
          ...args?.[1],
          query: args?.[0],
        });
      },
      responseTransformer: ({ data }: HttpResponse<QueryAddOnsResponse>) => {
        const transformedData = renameKeysFromRESTResponseToSDKResponse(
          transformPaths(data, [])
        );

        return {
          items: transformedData?.addOns,
          pagingMetadata: transformedData?.pagingMetadata,
        };
      },
      errorTransformer: (err: unknown) => {
        const transformedError = sdkTransformError(err, {
          spreadPathsToArguments: {},
          explicitPathsToArguments: { query: '$[0]' },
          singleArgumentUnchanged: false,
        });

        throw transformedError;
      },
      pagingMethod: 'CURSOR',
      transformationPaths: {},
    }
  );
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface AddOnsQueryResult extends QueryCursorResult {
  items: AddOn[];
  query: AddOnsQueryBuilder;
  next: () => Promise<AddOnsQueryResult>;
  prev: () => Promise<AddOnsQueryResult>;
}

export interface AddOnsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  eq: (
    propertyName:
      | 'durationInMinutes'
      | 'maxQuantity'
      | '_id'
      | 'name'
      | 'price.value'
      | 'price.currency'
      | 'price.formattedValue'
      | 'taxGroup',
    value: any
  ) => AddOnsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ne: (
    propertyName:
      | 'durationInMinutes'
      | 'maxQuantity'
      | '_id'
      | 'name'
      | 'price.value'
      | 'price.currency'
      | 'price.formattedValue'
      | 'taxGroup',
    value: any
  ) => AddOnsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ge: (
    propertyName:
      | 'durationInMinutes'
      | 'maxQuantity'
      | '_id'
      | 'name'
      | 'price.value'
      | 'price.currency'
      | 'price.formattedValue'
      | 'taxGroup',
    value: any
  ) => AddOnsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  gt: (
    propertyName:
      | 'durationInMinutes'
      | 'maxQuantity'
      | '_id'
      | 'name'
      | 'price.value'
      | 'price.currency'
      | 'price.formattedValue'
      | 'taxGroup',
    value: any
  ) => AddOnsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  le: (
    propertyName:
      | 'durationInMinutes'
      | 'maxQuantity'
      | '_id'
      | 'name'
      | 'price.value'
      | 'price.currency'
      | 'price.formattedValue'
      | 'taxGroup',
    value: any
  ) => AddOnsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  lt: (
    propertyName:
      | 'durationInMinutes'
      | 'maxQuantity'
      | '_id'
      | 'name'
      | 'price.value'
      | 'price.currency'
      | 'price.formattedValue'
      | 'taxGroup',
    value: any
  ) => AddOnsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   * @documentationMaturity preview
   */
  startsWith: (
    propertyName:
      | '_id'
      | 'name'
      | 'price.value'
      | 'price.currency'
      | 'price.formattedValue'
      | 'taxGroup',
    value: string
  ) => AddOnsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   * @documentationMaturity preview
   */
  hasSome: (
    propertyName:
      | 'durationInMinutes'
      | 'maxQuantity'
      | '_id'
      | 'name'
      | 'price.value'
      | 'price.currency'
      | 'price.formattedValue'
      | 'taxGroup',
    value: any[]
  ) => AddOnsQueryBuilder;
  /** @documentationMaturity preview */
  in: (
    propertyName:
      | 'durationInMinutes'
      | 'maxQuantity'
      | '_id'
      | 'name'
      | 'price.value'
      | 'price.currency'
      | 'price.formattedValue'
      | 'taxGroup',
    value: any
  ) => AddOnsQueryBuilder;
  /** @documentationMaturity preview */
  exists: (
    propertyName:
      | 'durationInMinutes'
      | 'maxQuantity'
      | '_id'
      | 'name'
      | 'price.value'
      | 'price.currency'
      | 'price.formattedValue'
      | 'taxGroup',
    value: boolean
  ) => AddOnsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments.
   * @documentationMaturity preview
   */
  ascending: (
    ...propertyNames: Array<
      | 'durationInMinutes'
      | 'maxQuantity'
      | '_id'
      | 'name'
      | 'price.value'
      | 'price.currency'
      | 'price.formattedValue'
      | 'taxGroup'
    >
  ) => AddOnsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments.
   * @documentationMaturity preview
   */
  descending: (
    ...propertyNames: Array<
      | 'durationInMinutes'
      | 'maxQuantity'
      | '_id'
      | 'name'
      | 'price.value'
      | 'price.currency'
      | 'price.formattedValue'
      | 'taxGroup'
    >
  ) => AddOnsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object.
   * @documentationMaturity preview
   */
  limit: (limit: number) => AddOnsQueryBuilder;
  /** @param cursor - A pointer to specific record
   * @documentationMaturity preview
   */
  skipTo: (cursor: string) => AddOnsQueryBuilder;
  /** @documentationMaturity preview */
  find: () => Promise<AddOnsQueryResult>;
}

/**
 * @hidden
 * @fqn wix.addons.v1.AddOnsService.QueryAddOns
 * @requiredField query
 */
export async function typedQueryAddOns(
  query: AddOnQuery
): Promise<
  NonNullablePaths<
    QueryAddOnsResponse,
    | `addOns`
    | `addOns.${number}.durationInMinutes`
    | `addOns.${number}.maxQuantity`
    | `addOns.${number}.price.value`
    | `addOns.${number}.price.currency`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts = ambassadorWixAddonsV1AddOn.queryAddOns(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface AddOnQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: [
        '_id',
        'durationInMinutes',
        'maxQuantity',
        'name',
        'price.currency',
        'price.formattedValue',
        'price.value',
        'taxGroup'
      ];
      operators: '*';
      sort: 'BOTH';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<AddOn, AddOnQuerySpec>;
export type AddOnQuery = {
  /** 
  Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to return in the results. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object in the following format:
  `"filter" : {
  "fieldName1": "value1",
  "fieldName2":{"$operator":"value2"}
  }`
  Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`

  Refer to [supported filters](https://dev.wix.com/docs/rest/business-solutions/bookings/services/add-ons/supported-filters) for a complete list of supported fields.  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object in the following format:
  `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]` 
  @maxSize: 5 
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: {
    ...createQueryUtils<AddOn, AddOnQuerySpec, AddOnQuery>(),
  },
};

/**
 * Creates multiple add-ons in a single request.
 * @param addOns - List of add-ons to create.
 * @public
 * @documentationMaturity preview
 * @requiredField addOns
 * @permissionId ADD_ONS.ADD_ON_CREATE
 * @applicableIdentity APP
 * @fqn wix.addons.v1.AddOnsService.BulkCreateAddOns
 */
export async function bulkCreateAddOns(
  addOns: AddOn[],
  options?: BulkCreateAddOnsOptions
): Promise<
  NonNullablePaths<
    BulkCreateAddOnsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.item.durationInMinutes`
    | `results.${number}.item.maxQuantity`
    | `results.${number}.item.price.value`
    | `results.${number}.item.price.currency`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    addOns: addOns,
    returnEntity: options?.returnEntity,
  });

  const reqOpts = ambassadorWixAddonsV1AddOn.bulkCreateAddOns(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          addOns: '$[0]',
          returnEntity: '$[1].returnEntity',
        },
        singleArgumentUnchanged: false,
      },
      ['addOns', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkCreateAddOnsOptions {
  /** Whether to return the created add-ons in the response. */
  returnEntity?: boolean;
}

/**
 * Updates multiple add-ons in a single request.
 * @param addOns - List of add-ons to update.
 * @public
 * @documentationMaturity preview
 * @requiredField addOns
 * @requiredField addOns.addOn._id
 * @requiredField addOns.addOn.revision
 * @permissionId ADD_ONS.ADD_ON_UPDATE
 * @applicableIdentity APP
 * @fqn wix.addons.v1.AddOnsService.BulkUpdateAddOns
 */
export async function bulkUpdateAddOns(
  addOns: NonNullablePaths<MaskedAddOn, `addOn._id` | `addOn.revision`, 3>[],
  options?: BulkUpdateAddOnsOptions
): Promise<
  NonNullablePaths<
    BulkUpdateAddOnsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.item.durationInMinutes`
    | `results.${number}.item.maxQuantity`
    | `results.${number}.item.price.value`
    | `results.${number}.item.price.currency`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    addOns: addOns,
    returnEntity: options?.returnEntity,
  });

  const reqOpts = ambassadorWixAddonsV1AddOn.bulkUpdateAddOns(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          addOns: '$[0]',
          returnEntity: '$[1].returnEntity',
        },
        singleArgumentUnchanged: false,
      },
      ['addOns', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateAddOnsOptions {
  /** Whether to return the updated add-ons in the response. */
  returnEntity?: boolean;
}

/**
 * Deletes multiple add-ons in a single request.
 * @param addOnIds - IDs of add-ons to delete.
 * @public
 * @documentationMaturity preview
 * @requiredField addOnIds
 * @permissionId ADD_ONS.ADD_ON_DELETE
 * @applicableIdentity APP
 * @fqn wix.addons.v1.AddOnsService.BulkDeleteAddOns
 */
export async function bulkDeleteAddOns(
  addOnIds: string[]
): Promise<
  NonNullablePaths<
    BulkDeleteAddOnsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ addOnIds: addOnIds });

  const reqOpts = ambassadorWixAddonsV1AddOn.bulkDeleteAddOns(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { addOnIds: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['addOnIds']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
